import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import {
  approveSubjectSuggestion,
  getAdminSubjectSuggestions,
  rejectSubjectSuggestion,
} from "../../services/subjectSuggestionsService";

const STATUS_OPTIONS = ["pending", "approved", "rejected"];
const STATUS_STYLES = {
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  pending: "bg-amber-100 text-amber-800",
};

const fullName = (tutor) =>
  [tutor?.firstName, tutor?.lastName].filter(Boolean).join(" ") ||
  "Unknown Tutor";

const formatDate = (date) =>
  (date &&
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })) ||
  "—";

const StatusBadge = ({ suggestion }) => (
  <div className="min-w-[130px]">
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${
        STATUS_STYLES[suggestion.status] ?? STATUS_STYLES.pending
      }`}
    >
      {suggestion.status}
    </span>
    {suggestion.status === "rejected" && suggestion.rejectionReason && (
      <p className="mt-1 max-w-[220px] whitespace-normal text-xs text-gray-500">
        {suggestion.rejectionReason}
      </p>
    )}
  </div>
);

const RejectSuggestionDialog = ({
  dialog,
  processing,
  onReasonChange,
  onClose,
  onConfirm,
}) => {
  const { suggestion, reason } = dialog;
  const tutorName = suggestion?.tutor && fullName(suggestion.tutor);

  return (
    <Dialog
      open={Boolean(suggestion)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Reject Subject Suggestion</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          <p className="text-sm text-gray-600">
            You are rejecting <strong>{suggestion?.name}</strong>
            {tutorName && (
              <>
                {" "}
                suggested by <strong>{tutorName}</strong>
              </>
            )}
            . The tutor will receive this reason in a notification.
          </p>
          <TextField
            label="Rejection Reason"
            required
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={reason}
            onChange={({ target }) => onReasonChange(target.value)}
            disabled={processing}
            placeholder="Explain why this subject was not approved"
            helperText="Required. This message will be shared with the tutor."
            inputProps={{ maxLength: 1000 }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={processing || !reason.trim()}
        >
          {processing ? "Rejecting..." : "Reject Suggestion"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SubjectSuggestionsSection = ({
  pendingCount,
  setPendingCount,
  showMessage,
  refreshKey,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({
    suggestion: null,
    reason: "",
  });

  useEffect(() => {
    let active = true;
    setLoading(true);

    getAdminSubjectSuggestions({ status, page: 1, limit: 100 })
      .then((response) => {
        if (!active) return;
        setSuggestions(Array.isArray(response?.items) ? response.items : []);
        status === "pending" &&
          setPendingCount(Number(response?.total) || 0);
      })
      .catch((error) => {
        console.error("Failed to fetch subject suggestions:", error);
        active && setSuggestions([]);
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [refreshKey, setPendingCount, status]);

  const updateReviewedSuggestion = (id, nextStatus, response) => {
    setSuggestions((current) =>
      status === "pending"
        ? current.filter((item) => item.id !== id)
        : current.map((item) =>
            item.id === id
              ? { ...item, ...response?.suggestion, status: nextStatus }
              : item,
          ),
    );
    setPendingCount((count) =>
      typeof count === "number" ? Math.max(0, count - 1) : count,
    );
  };

  const reviewSuggestion = async (suggestion, nextStatus, reason) => {
    if (!suggestion?.id || updatingId) return;

    const action =
      nextStatus === "approved"
        ? () => approveSubjectSuggestion(suggestion.id)
        : () => rejectSubjectSuggestion(suggestion.id, reason);

    setUpdatingId(suggestion.id);
    try {
      const response = await action();
      updateReviewedSuggestion(suggestion.id, nextStatus, response);
      showMessage({
        message:
          response?.message || `Subject suggestion ${nextStatus}`,
        type: "success",
      });
      nextStatus === "rejected" &&
        setRejectDialog({ suggestion: null, reason: "" });
    } catch (error) {
      console.error(`Failed to ${nextStatus} subject suggestion:`, error);
      showMessage({
        message:
          error?.message || `Failed to ${nextStatus} subject suggestion`,
        type: "error",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const closeRejectDialog = () =>
    !updatingId && setRejectDialog({ suggestion: null, reason: "" });

  const rows = useMemo(
    () =>
      suggestions.map((suggestion) => ({
        ...suggestion,
        tutorName: fullName(suggestion.tutor),
        submittedAt: formatDate(suggestion.createdAt),
      })),
    [suggestions],
  );

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Subject Suggestions
          </h2>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <span className="text-sm text-gray-500">
              Pending: {pendingCount ?? "—"}
            </span>
            <select
              value={status}
              onChange={({ target }) => setStatus(target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-medium-blue focus:outline-none focus:ring-2 focus:ring-brand-medium-blue"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option[0].toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-medium-blue" />
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white py-10 text-center text-gray-500">
            No {status} subject suggestions.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brand-light-blue">
                <tr>
                  {[
                    "Tutor",
                    "Suggested Subject",
                    "Category",
                    "Levels",
                    "Submitted",
                    "Status",
                    "",
                  ].map((header, index) => (
                    <th
                      key={header || `actions-${index}`}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-brand-dark-blue"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((suggestion) => {
                  const processing = updatingId === suggestion.id;

                  return (
                    <tr
                      key={suggestion.id}
                      className="hover:bg-brand-light-blue/40"
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="min-w-[170px]">
                          <div className="font-medium text-gray-900">
                            {suggestion.tutorName}
                          </div>
                          <div className="mt-0.5 text-xs text-gray-500">
                            {suggestion.tutor?.email || "Email unavailable"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="min-w-[220px] max-w-md">
                          <div className="font-medium text-gray-900">
                            {suggestion.name}
                          </div>
                          <p className="mt-1 whitespace-normal text-xs leading-5 text-gray-500">
                            {suggestion.description ||
                              "No description provided."}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {suggestion.category}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex min-w-[150px] flex-wrap gap-1">
                          {(suggestion.gradeLevels ?? []).map((level) => (
                            <span
                              key={level}
                              className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800"
                            >
                              {level}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {suggestion.submittedAt}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <StatusBadge suggestion={suggestion} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {suggestion.status === "pending" && (
                          <div className="flex min-w-[185px] justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setRejectDialog({
                                  suggestion,
                                  reason: "",
                                })
                              }
                              disabled={processing}
                              className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                reviewSuggestion(suggestion, "approved")
                              }
                              disabled={processing}
                              className="rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {processing ? "Processing..." : "Approve"}
                            </button>
                          </div>
                        )}
                        {suggestion.status !== "pending" && (
                          <span className="text-xs text-gray-400">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <RejectSuggestionDialog
        dialog={rejectDialog}
        processing={updatingId === rejectDialog.suggestion?.id}
        onReasonChange={(reason) =>
          setRejectDialog((current) => ({ ...current, reason }))
        }
        onClose={closeRejectDialog}
        onConfirm={() =>
          reviewSuggestion(
            rejectDialog.suggestion,
            "rejected",
            rejectDialog.reason.trim(),
          )
        }
      />
    </>
  );
};
